import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PrescriptionService } from "../../services/prescription.service";
import { LoaderService } from "../../services/loader.service";
import { Medicine } from 'src/interfaces/medicine';
import { DosageType } from 'src/interfaces/dosage-type';
import { MedicineDosagePackageType } from 'src/interfaces/medicine-dosage-package-type';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { AlertService } from 'src/core/services/alert.service';
import { OrderMedicineItem } from 'src/interfaces/order-medicine-item';

@Component({
  selector: 'core-add-order-medicine',
  templateUrl: './add-order-medicine.component.html',
  styleUrls: ['./add-order-medicine.component.scss']
})
export class AddOrderMedicineComponent implements OnInit {

  @Output() onMedicineAdd = new EventEmitter<OrderMedicineItem>();

  addOrderMedicineForm: FormGroup = this._formBuilder.group({
    medicineFilter: ['', Validators.required],
    medicine: ['', Validators.required],
    dosageType: [{ value: '', disabled: true }, Validators.required],
    packageType: [{ value: '', disabled: true }, Validators.required]
  });

  medicine: Medicine[] = [];
  medicinePage: number = 1;
  medicineLimit: number = 30;
  dosageTypes: DosageType[] = [];
  medicineDosageTypePackageTypes: MedicineDosagePackageType[] = [];
  filteredMedicine: Observable<Medicine[]>;
  selectedMedicine: Medicine | null = null;
  selectedDosageType: DosageType | null = null;
  selectedMedicineDosagePackageType: MedicineDosagePackageType | null = null;

  isLoading: boolean = false;
  constructor(
    private _formBuilder: FormBuilder,
    private prescriptionService: PrescriptionService,
    private loaderService: LoaderService,
    private _alertService: AlertService
  ) { }

  ngOnInit(): void {

    this
      .loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this
      .addOrderMedicineForm
      .controls['medicineFilter']
      .valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.getMedicine(value);
      });
  }

  async getMedicine(value: string) {
    this.medicine = await this
      .prescriptionService
      .getMedicine(value, this.medicinePage, this.medicineLimit);
  }

  medicineSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedMedicine = event.option.value;

    if (!this.selectedMedicine) return;

    this.addOrderMedicineForm.patchValue({
      medicineFilter: this.selectedMedicine.name,
      medicine: this.selectedMedicine,
      dosageType: null,
      packageType: null
    });
    this.getDosageTypes(this.selectedMedicine.id);
  }

  getDosageTypes(medicineId: number) {
    this
      .prescriptionService
      .getMedicineDosageTypes(medicineId)
      .subscribe(dosageTypes => {
        
        this.dosageTypes = dosageTypes;
        if(this.dosageTypes.length  === 0){
          this._alertService.errorAlert("This medicine does not have any dosage types");
        } else {
          this.addOrderMedicineForm.controls['dosageType'].enable();
        }
      });
  }

  getMedicineDosagePackageTypes(event: MatSelectChange) {
    const dosageType: DosageType = event.value;

    this.selectedDosageType = dosageType;
    if (!this.selectedDosageType || !this.selectedMedicine) return;

    this
      .prescriptionService
      .getMedicineDosagePackageTypes(this.selectedMedicine.id, this.selectedDosageType.id)
      .subscribe(medicineDosagePackageTypes => {
        this.medicineDosageTypePackageTypes = medicineDosagePackageTypes;
        if (this.medicineDosageTypePackageTypes.length === 0) {
          this._alertService.errorAlert("This medicine does not have any package types");
        } else {
          this.addOrderMedicineForm.controls['packageType'].enable();
        }
      })
  }

  packageTypeSelected(event: MatSelectChange) {
    const medicineDosagePackageType: MedicineDosagePackageType = event.value;

    if (medicineDosagePackageType) {
      this.selectedMedicineDosagePackageType = medicineDosagePackageType;
    }
  }

  addOrderItem() {
    if (!this.addOrderMedicineForm.valid) return;

    const values = {
      ...this.addOrderMedicineForm.value
    }

    const medicine: Medicine = values.medicine;
    const medicineDosagePackageType: MedicineDosagePackageType = values.packageType;
    const dosageType: DosageType = values.dosageType;

    this.onMedicineAdd.emit({
      id: null,
      medicineDosagePackageType,
      dosageType,
      medicine,
      status: null,
      medicine_dosage_package_type_id: medicineDosagePackageType.id,
      deleted_at: undefined
    });

    this.reset();
  }

  reset() {
    Object
      .keys(this.addOrderMedicineForm.controls)
      .forEach((key) => {
        const control = this.addOrderMedicineForm.controls[key];
        control.markAsPristine();
        control.markAsTouched();
      })
    this.addOrderMedicineForm.reset();
    this.selectedMedicine = null;
    this.selectedDosageType = null;
    this.selectedMedicineDosagePackageType = null;
    this.dosageTypes = [];
    this.medicineDosageTypePackageTypes = [];

    //  disable controls
    this.addOrderMedicineForm.controls['dosageType'].disable();
    this.addOrderMedicineForm.controls['packageType'].disable();
  }
}
